import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Stack,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Autocomplete,
  Alert,
  MenuItem,
  Divider,
  Tabs,
  Tab,
  LinearProgress,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import WarningIcon from '@mui/icons-material/Warning'
import SecurityIcon from '@mui/icons-material/Security'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import InfoIcon from '@mui/icons-material/Info'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format, subDays } from 'date-fns'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import TransactionDetailModal from '@/components/TransactionDetailModal'
import { userAnalyticsService, UserRiskAnalysisResponse, ResolvedMsisdn } from '@/services/userAnalyticsService'
import { Deposit, Withdrawal } from '@/types/transactions'
import { exportToCSV, exportToPDF } from '@/utils/export'
import { useSearchParams } from 'react-router-dom'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

const COLORS = {
  low: '#4caf50',
  medium: '#ff9800',
  high: '#f44336',
  critical: '#d32f2f',
}

const UserActivity = () => {
  const [searchParams] = useSearchParams()
  const [msisdn, setMsisdn] = useState('')
  const [searchMsisdn, setSearchMsisdn] = useState('')
  const [error, setError] = useState('')
  const [filters, setFilters] = useState({
    startDate: format(subDays(new Date(), 90), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  })
  const [compareMode, setCompareMode] = useState(false)
  const [compareFilters, setCompareFilters] = useState({
    startDate: format(subDays(new Date(), 180), 'yyyy-MM-dd'),
    endDate: format(subDays(new Date(), 90), 'yyyy-MM-dd'),
  })
  const [selectedTransaction, setSelectedTransaction] = useState<Deposit | Withdrawal | null>(null)
  const [modalType, setModalType] = useState<'deposit' | 'withdrawal'>('deposit')
  const [modalOpen, setModalOpen] = useState(false)
  const [msisdnOptions, setMsisdnOptions] = useState<ResolvedMsisdn[]>([])
  const [resolvingMsisdn, setResolvingMsisdn] = useState(false)
  const [riskAnalysis, setRiskAnalysis] = useState<UserRiskAnalysisResponse | null>(null)
  const [compareRiskAnalysis, setCompareRiskAnalysis] = useState<UserRiskAnalysisResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [tabValue, setTabValue] = useState(0)

  useEffect(() => {
    const qp = (searchParams.get('msisdn') || '').trim()
    if (qp) {
      setMsisdn(qp)
      setSearchMsisdn(qp)
    }
  }, [searchParams])

  useEffect(() => {
    const query = msisdn.trim()
    if (!query || query.length < 5) {
      setMsisdnOptions([])
      return
    }
    let cancelled = false
    setResolvingMsisdn(true)
    const timer = setTimeout(async () => {
      try {
        const items = await userAnalyticsService.resolveMsisdn(query, 20)
        if (!cancelled) setMsisdnOptions(items || [])
      } catch {
        if (!cancelled) setMsisdnOptions([])
      } finally {
        if (!cancelled) setResolvingMsisdn(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [msisdn])

  const fetchRiskAnalysis = useCallback(
    async (compare = false) => {
      if (!searchMsisdn) {
        if (!compare) setRiskAnalysis(null)
        return
      }
      setLoading(true)
      setError('')
      try {
        const startDate = compare ? compareFilters.startDate : filters.startDate
        const endDate = compare ? compareFilters.endDate : filters.endDate
        const result = await userAnalyticsService.fetchRiskAnalysis(searchMsisdn, startDate, endDate)
        if (compare) {
          setCompareRiskAnalysis(result)
        } else {
          setRiskAnalysis(result)
        }
      } catch (err: any) {
        console.error('Failed to fetch risk analysis', err)
        setError(err.response?.data?.message || 'Failed to load risk analysis')
        if (!compare) setRiskAnalysis(null)
      } finally {
        setLoading(false)
      }
    },
    [searchMsisdn, filters, compareFilters]
  )

  useEffect(() => {
    fetchRiskAnalysis(false)
  }, [fetchRiskAnalysis])

  const handleSearch = async () => {
    setError('')
    const q = msisdn.trim()
    if (!q) return
    // If user enters masked form (e.g. 2547****854), resolve it first.
    if (q.includes('*')) {
      setLoading(true)
      try {
        const matches = await userAnalyticsService.resolveMsisdn(q, 20)
        setMsisdnOptions(matches || [])
        if (!matches || matches.length === 0) {
          setError(`No MSISDN found for pattern ${q}`)
          return
        }
        // Auto-pick latest active match so analysis starts immediately.
        const picked = matches[0].msisdn
        setMsisdn(picked)
        setSearchMsisdn(picked)
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to resolve masked MSISDN')
      } finally {
        setLoading(false)
      }
      return
    }
    setSearchMsisdn(q)
  }

  const handleCompare = () => {
    if (compareMode) {
      fetchRiskAnalysis(true)
    }
  }

  const allTransactions = useMemo(() => {
    if (!riskAnalysis) return []
    const depositsWithType = riskAnalysis.deposits.map((d: any) => ({ 
      ...d, 
      type: 'Deposit' as const,
      created_at: d.createdAt || d.created_at, // Normalize to created_at for consistency
    }))
    const withdrawalsWithType = riskAnalysis.withdrawals.map((w: any) => ({ 
      ...w, 
      type: 'Withdrawal' as const,
      created_at: w.createdAt || w.created_at, // Normalize to created_at for consistency
    }))
    return [...depositsWithType, ...withdrawalsWithType]
      .filter((tx) => tx.created_at && !isNaN(new Date(tx.created_at).getTime()))
      .sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
  }, [riskAnalysis])

  const handleRowClick = (tx: typeof allTransactions[number]) => {
    if (tx.type === 'Deposit') {
      setSelectedTransaction(tx as Deposit)
      setModalType('deposit')
      setModalOpen(true)
    } else {
      setSelectedTransaction(tx as Withdrawal)
      setModalType('withdrawal')
      setModalOpen(true)
    }
  }

  const handleExportCSV = () => {
    const columns = [
      { header: 'Type', field: 'type' },
      { header: 'Amount', field: 'amount' },
      { header: 'Risk Score', field: 'riskScore' },
      { header: 'Risk Level', field: 'riskLevel' },
      { header: 'Risk Flags', field: 'riskFlags' },
      { header: 'Channel', field: 'channel' },
      { header: 'Status', field: 'status' },
      { header: 'Paybill', field: 'paybill' },
      { header: 'Date', field: 'created_at' },
    ]
    exportToCSV(allTransactions, `user_risk_analysis_${searchMsisdn}_${new Date().toISOString().split('T')[0]}`, columns)
  }

  const handleExportPDF = () => {
    const columns = [
      { header: 'Type', field: 'type' },
      { header: 'Amount', field: 'amount' },
      { header: 'Risk Score', field: 'riskScore' },
      { header: 'Risk Level', field: 'riskLevel' },
      { header: 'Risk Flags', field: 'riskFlags' },
      { header: 'Channel', field: 'channel' },
      { header: 'Status', field: 'status' },
      { header: 'Paybill', field: 'paybill' },
      { header: 'Date', field: 'created_at' },
    ]
    exportToPDF(
      allTransactions,
      `user_risk_analysis_${searchMsisdn}_${new Date().toISOString().split('T')[0]}`,
      `User Risk Analysis Report - ${searchMsisdn}`,
      columns
    )
  }

  const getRiskColor = (level: string) => {
    switch (level?.toLowerCase()) {
      case 'critical':
        return COLORS.critical
      case 'high':
        return COLORS.high
      case 'medium':
        return COLORS.medium
      case 'low':
        return COLORS.low
      default:
        return '#9e9e9e'
    }
  }

  const getRiskChipColor = (level: string): 'error' | 'warning' | 'info' | 'success' | 'default' => {
    switch (level?.toLowerCase()) {
      case 'critical':
      case 'high':
        return 'error'
      case 'medium':
        return 'warning'
      case 'low':
        return 'success'
      default:
        return 'default'
    }
  }

  // Risk score chart data
  const riskScoreChartData = useMemo(() => {
    if (!riskAnalysis) return []
    return riskAnalysis.transactionRisks
      .filter((risk) => risk.date && !isNaN(new Date(risk.date).getTime()))
      .map((risk) => ({
        date: format(new Date(risk.date), 'MMM dd HH:mm'),
        riskScore: risk.riskScore,
        amount: risk.amount,
        type: risk.type,
        rawDate: risk.date,
      }))
      .sort((a, b) => new Date(a.rawDate).getTime() - new Date(b.rawDate).getTime())
  }, [riskAnalysis])

  // Risk level distribution
  const riskLevelDistribution = useMemo(() => {
    if (!riskAnalysis) return []
    const counts = riskAnalysis.riskMetrics.riskLevelCounts
    return [
      { name: 'Critical', value: counts.critical || 0, color: COLORS.critical },
      { name: 'High', value: counts.high || 0, color: COLORS.high },
      { name: 'Medium', value: counts.medium || 0, color: COLORS.medium },
      { name: 'Low', value: counts.low || 0, color: COLORS.low },
    ].filter((item) => item.value > 0)
  }, [riskAnalysis])

  // Risk flags data
  const riskFlagsData = useMemo(() => {
    if (!riskAnalysis) return []
    return Object.entries(riskAnalysis.riskFlags)
      .map(([flag, count]) => ({ flag, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [riskAnalysis])

  // Comparison data
  const comparisonData = useMemo(() => {
    if (!riskAnalysis || !compareRiskAnalysis) return null
    return {
      current: {
        overallRiskScore: riskAnalysis.overallRiskScore,
        totalTransactions: riskAnalysis.riskMetrics.totalTransactions,
        riskyTransactions: riskAnalysis.riskMetrics.riskyTransactions,
        averageRiskScore: riskAnalysis.riskMetrics.averageRiskScore,
      },
      compare: {
        overallRiskScore: compareRiskAnalysis.overallRiskScore,
        totalTransactions: compareRiskAnalysis.riskMetrics.totalTransactions,
        riskyTransactions: compareRiskAnalysis.riskMetrics.riskyTransactions,
        averageRiskScore: compareRiskAnalysis.riskMetrics.averageRiskScore,
      },
    }
  }, [riskAnalysis, compareRiskAnalysis])

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        User Risk Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Comprehensive risk assessment and fraud detection analysis for individual users
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Autocomplete
                fullWidth
                options={msisdnOptions}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.msisdn)}
                isOptionEqualToValue={(option, value) =>
                  (typeof option === 'string' ? option : option.msisdn) ===
                  (typeof value === 'string' ? value : value.msisdn)
                }
                filterOptions={(x) => x}
                value={msisdnOptions.find((o) => o.msisdn === msisdn) ?? null}
                inputValue={msisdn}
                onInputChange={(_e, value) => setMsisdn(value)}
                onChange={(_e, value) => {
                  if (value && typeof value !== 'string') {
                    setMsisdn(value.msisdn)
                    setSearchMsisdn(value.msisdn)
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.msisdn}>
                    <Stack width="100%" spacing={0.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">{option.msisdn}</Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            {option.transactionCount} tx
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                              if (navigator?.clipboard) {
                                navigator.clipboard.writeText(option.msisdn).catch(() => undefined)
                              }
                            }}
                          >
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Stack>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        D:{option.depositsCount ?? 0} / DD:{option.directDepositsCount ?? 0} / W:{option.withdrawalsCount ?? 0} | Net:{' '}
                        {(option.netAmount ?? 0).toFixed(2)}
                      </Typography>
                      {!!option.paybills?.length && (
                        <Typography variant="caption" color="text.secondary">
                          Paybills: {option.paybills.slice(0, 3).join(', ')}
                        </Typography>
                      )}
                      {!!option.externalUserIds?.length && (
                        <Typography variant="caption" color="text.secondary">
                          User IDs: {option.externalUserIds.slice(0, 2).join(', ')}
                        </Typography>
                      )}
                    </Stack>
                  </li>
                )}
                loading={resolvingMsisdn}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="MSISDN / Masked MSISDN"
                    placeholder="ex: 2547****854"
                    helperText="Poti cauta cu wildcard * si selecta numarul complet."
                  />
                )}
              />
              <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} disabled={loading}>
                Analyze Risk
              </Button>
            </Stack>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate ? new Date(filters.startDate) : null}
                  onChange={(date) =>
                    setFilters((prev) => ({ ...prev, startDate: date ? format(date, 'yyyy-MM-dd') : prev.startDate }))
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate ? new Date(filters.endDate) : null}
                  onChange={(date) =>
                    setFilters((prev) => ({ ...prev, endDate: date ? format(date, 'yyyy-MM-dd') : prev.endDate }))
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>

            <Divider />

            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant={compareMode ? 'contained' : 'outlined'}
                startIcon={<CompareArrowsIcon />}
                onClick={() => {
                  setCompareMode(!compareMode)
                  if (compareMode) {
                    setCompareRiskAnalysis(null)
                  }
                }}
              >
                Compare Periods
              </Button>
              {compareMode && (
                <>
                  <DatePicker
                    label="Compare Start"
                    value={compareFilters.startDate ? new Date(compareFilters.startDate) : null}
                    onChange={(date) =>
                      setCompareFilters((prev) => ({
                        ...prev,
                        startDate: date ? format(date, 'yyyy-MM-dd') : prev.startDate,
                      }))
                    }
                    slotProps={{ textField: { size: 'small' } }}
                  />
                  <DatePicker
                    label="Compare End"
                    value={compareFilters.endDate ? new Date(compareFilters.endDate) : null}
                    onChange={(date) =>
                      setCompareFilters((prev) => ({
                        ...prev,
                        endDate: date ? format(date, 'yyyy-MM-dd') : prev.endDate,
                      }))
                    }
                    slotProps={{ textField: { size: 'small' } }}
                  />
                  <Button variant="contained" onClick={handleCompare} disabled={loading}>
                    Compare
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </Paper>
      </LocalizationProvider>

      {loading && (
        <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Analyzing user risk profile...
          </Typography>
        </Paper>
      )}

      {riskAnalysis && !loading && (
        <>
          {/* Overall Risk Score Card */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ background: `linear-gradient(135deg, ${getRiskColor(riskAnalysis.overallRiskLevel)} 0%, ${getRiskColor(riskAnalysis.overallRiskLevel)}dd 100%)`, color: 'white' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                        Overall Risk Score
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 'bold', mt: 1 }}>
                        {riskAnalysis.overallRiskScore.toFixed(1)}
                      </Typography>
                      <Chip
                        label={riskAnalysis.overallRiskLevel.toUpperCase()}
                        size="small"
                        sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }}
                      />
                    </Box>
                    <SecurityIcon sx={{ fontSize: 60, opacity: 0.3 }} />
                  </Stack>
                  {riskAnalysis.isBlacklisted && (
                    <Alert severity="error" sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.2)' }}>
                      User is blacklisted
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Total Transactions
                      </Typography>
                      <Typography variant="h5">{riskAnalysis.riskMetrics.totalTransactions}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Risky Transactions
                      </Typography>
                      <Typography variant="h5" color="error.main">
                        {riskAnalysis.riskMetrics.riskyTransactions}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Avg Risk Score
                      </Typography>
                      <Typography variant="h5" color={getRiskColor(riskAnalysis.overallRiskLevel)}>
                        {riskAnalysis.riskMetrics.averageRiskScore.toFixed(1)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Max Risk Score
                      </Typography>
                      <Typography variant="h5" color="error.main">
                        {riskAnalysis.riskMetrics.maxRiskScore}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Behavioral Risks */}
          {riskAnalysis.behavioralRisks.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <WarningIcon color="warning" />
                <Typography variant="h6">Behavioral Risk Indicators</Typography>
              </Stack>
              <Grid container spacing={2}>
                {riskAnalysis.behavioralRisks.map((risk, idx) => (
                  <Grid item xs={12} md={6} key={idx}>
                    <Alert
                      severity={risk.severity === 'critical' || risk.severity === 'high' ? 'error' : 'warning'}
                      icon={<InfoIcon />}
                    >
                      <Typography variant="subtitle2">{risk.description}</Typography>
                    </Alert>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Comparison */}
          {compareMode && comparisonData && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Period Comparison
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Current Period
                      </Typography>
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="body2">Overall Risk Score</Typography>
                          <Typography variant="h6" color={getRiskColor(riskAnalysis.overallRiskLevel)}>
                            {comparisonData.current.overallRiskScore.toFixed(1)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2">Risky Transactions</Typography>
                          <Typography variant="h6">
                            {comparisonData.current.riskyTransactions} / {comparisonData.current.totalTransactions}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Compare Period
                      </Typography>
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="body2">Overall Risk Score</Typography>
                          <Typography variant="h6" color={getRiskColor(compareRiskAnalysis!.overallRiskLevel)}>
                            {comparisonData.compare.overallRiskScore.toFixed(1)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2">Risky Transactions</Typography>
                          <Typography variant="h6">
                            {comparisonData.compare.riskyTransactions} / {comparisonData.compare.totalTransactions}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        Risk Score Change
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center">
                        {comparisonData.current.overallRiskScore > comparisonData.compare.overallRiskScore ? (
                          <>
                            <TrendingUpIcon color="error" />
                            <Typography variant="h6" color="error">
                              +{(comparisonData.current.overallRiskScore - comparisonData.compare.overallRiskScore).toFixed(1)} points
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Risk increased
                            </Typography>
                          </>
                        ) : (
                          <>
                            <TrendingDownIcon color="success" />
                            <Typography variant="h6" color="success.main">
                              {(comparisonData.current.overallRiskScore - comparisonData.compare.overallRiskScore).toFixed(1)} points
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Risk decreased
                            </Typography>
                          </>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                <Tab label="Risk Overview" />
                <Tab label="Risk Flags" />
                <Tab label="Transactions" />
                <Tab label="Risk Trends" />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <Box sx={{ p: 3 }}>
                <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                  <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportCSV}>
                    Export CSV
                  </Button>
                  <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleExportPDF}>
                    Export PDF
                  </Button>
                </Stack>

                <Grid container spacing={3}>
                  {/* Risk Level Distribution */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Risk Level Distribution
                        </Typography>
                        {riskLevelDistribution.length > 0 ? (
                          <Box sx={{ height: 300 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={riskLevelDistribution}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                  outerRadius={80}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {riskLevelDistribution.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                  ))}
                                </Pie>
                                <RechartsTooltip />
                              </PieChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
                            No risk data available
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Top Risk Flags */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Top Risk Flags
                        </Typography>
                        {riskFlagsData.length > 0 ? (
                          <Box sx={{ height: 300 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={riskFlagsData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis dataKey="flag" type="category" width={150} />
                                <RechartsTooltip />
                                <Bar dataKey="count" fill="#f44336" />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
                            No risk flags detected
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Risk Score Timeline */}
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Risk Score Timeline
                        </Typography>
                        {riskScoreChartData.length > 0 ? (
                          <Box sx={{ height: 300 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={riskScoreChartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <RechartsTooltip />
                                <Legend />
                                <Line type="monotone" dataKey="riskScore" stroke="#f44336" name="Risk Score" strokeWidth={2} />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
                            No risk timeline data available
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Risk Flags & Triggered Rules
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          Risk Flags ({Object.keys(riskAnalysis.riskFlags).length})
                        </Typography>
                        <Stack spacing={1}>
                          {Object.entries(riskAnalysis.riskFlags)
                            .sort(([, a], [, b]) => b - a)
                            .map(([flag, count]) => (
                              <Box key={flag} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2">{flag}</Typography>
                                <Chip label={count} size="small" color="error" />
                              </Box>
                            ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          Triggered Rules ({Object.keys(riskAnalysis.triggeredRules).length})
                        </Typography>
                        <Stack spacing={1}>
                          {Object.entries(riskAnalysis.triggeredRules)
                            .sort(([, a], [, b]) => b - a)
                            .map(([rule, count]) => (
                              <Box key={rule} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2">{rule}</Typography>
                                <Chip label={count} size="small" color="warning" />
                              </Box>
                            ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Box sx={{ p: 3 }}>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportCSV}>
                    Export CSV
                  </Button>
                  <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleExportPDF}>
                    Export PDF
                  </Button>
                </Stack>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Risk Score</TableCell>
                        <TableCell>Risk Level</TableCell>
                        <TableCell>Risk Flags</TableCell>
                        <TableCell>Channel</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allTransactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No transactions found for this user in the selected period.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        allTransactions.map((tx, idx) => (
                          <TableRow key={idx} hover sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(tx)}>
                            <TableCell>
                              <Chip
                                label={tx.type}
                                color={tx.type === 'Withdrawal' ? 'secondary' : 'primary'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>KES {tx.amount.toFixed(2)}</TableCell>
                            <TableCell>
                              {tx.riskScore !== undefined && tx.riskScore !== null ? (
                                <Chip
                                  label={tx.riskScore}
                                  size="small"
                                  sx={{ bgcolor: getRiskColor(tx.riskLevel || 'low'), color: 'white' }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  N/A
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {tx.riskLevel ? (
                                <Chip label={tx.riskLevel.toUpperCase()} size="small" color={getRiskChipColor(tx.riskLevel)} />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  N/A
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {tx.riskFlags && tx.riskFlags.length > 0 ? (
                                <Tooltip
                                  title={
                                    <Box>
                                      {tx.riskFlags.map((flag: any, flagIdx: number) => {
                                        const flagText = typeof flag === 'string' ? flag : flag.flag || flag
                                        const flagDesc = typeof flag === 'object' && flag.description ? flag.description : ''
                                        const ruleName = typeof flag === 'object' && flag.ruleName ? flag.ruleName : ''
                                        return (
                                          <Box key={flagIdx} sx={{ mb: 1, maxWidth: 400 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                              {flagText}
                                              {ruleName && ` (${ruleName})`}
                                            </Typography>
                                            {flagDesc && (
                                              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.9)' }}>
                                                {flagDesc}
                                              </Typography>
                                            )}
                                          </Box>
                                        )
                                      })}
                                    </Box>
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <Chip
                                    label={`${tx.riskFlags.length} flag(s)`}
                                    size="small"
                                    color="warning"
                                    sx={{ cursor: 'help' }}
                                  />
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  None
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {typeof (tx as any).channel === 'string' 
                                ? (tx as any).channel 
                                : (tx as any).channel?.name || 'N/A'}
                            </TableCell>
                            <TableCell>
                            <Chip
                              label={
                                typeof (tx as any).status === 'string'
                                  ? (tx as any).status
                                  : (tx as any).status?.name || 'N/A'
                              }
                              color={
                                (typeof (tx as any).status === 'string' 
                                  ? (tx as any).status 
                                  : (tx as any).status?.name) === 'COMPLETED'
                                  ? 'success'
                                  : (typeof (tx as any).status === 'string' 
                                      ? (tx as any).status 
                                      : (tx as any).status?.name) === 'FAILED'
                                    ? 'error'
                                    : 'warning'
                              }
                              size="small"
                            />
                            </TableCell>
                            <TableCell>
                              {tx.created_at && !isNaN(new Date(tx.created_at).getTime())
                                ? format(new Date(tx.created_at), 'yyyy-MM-dd HH:mm')
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Risk Score Trends
                </Typography>
                {riskScoreChartData.length > 0 ? (
                  <Box sx={{ height: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={riskScoreChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Line type="monotone" dataKey="riskScore" stroke="#f44336" name="Risk Score" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
                    No trend data available
                  </Typography>
                )}
              </Box>
            </TabPanel>
          </Paper>

          <TransactionDetailModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            transaction={selectedTransaction}
            type={modalType}
          />
        </>
      )}

      {!riskAnalysis && !loading && searchMsisdn && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No risk analysis data available. Please search for a user to begin analysis.
          </Typography>
        </Paper>
      )}
    </Box>
  )
}

export default UserActivity
